/**
 * ServiceTypesManager Component
 *
 * Super Admin component for managing service types.
 * Allows viewing, adding, editing, and deactivating service types
 * that organizations can select during registration.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Search,
  Refresh,
  CloudDownload,
  Category,
} from '@mui/icons-material';
import {
  adminServiceTypesApi,
  ServiceType,
} from '../../services/admin';
import { getErrorMessage } from '../../services/api';
import { ConfirmDialog } from '../common/ConfirmDialog';

export default function ServiceTypesManager() {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Add/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    isActive: true,
  });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceTypeToDelete, setServiceTypeToDelete] = useState<ServiceType | null>(null);

  // Fetch service types
  const fetchServiceTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminServiceTypesApi.list();
      setServiceTypes(result.serviceTypes);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceTypes();
  }, [fetchServiceTypes]);

  // Open add dialog
  const handleOpenAddDialog = () => {
    setEditingServiceType(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setFormData({
      name: serviceType.name,
      slug: serviceType.slug,
      description: serviceType.description || '',
      icon: serviceType.icon || '',
      isActive: serviceType.isActive,
    });
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingServiceType(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      isActive: true,
    });
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      // Only auto-generate slug if not editing or if slug was empty
      slug: !editingServiceType || !prev.slug
        ? name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
        : prev.slug,
    }));
  };

  // Save service type (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      if (editingServiceType) {
        // Update existing
        const result = await adminServiceTypesApi.update(editingServiceType._id, {
          name: formData.name.trim(),
          slug: formData.slug.trim() || undefined,
          description: formData.description.trim() || undefined,
          icon: formData.icon.trim() || undefined,
          isActive: formData.isActive,
        });
        setSuccess(result.message);
      } else {
        // Create new
        const result = await adminServiceTypesApi.create({
          name: formData.name.trim(),
          slug: formData.slug.trim() || undefined,
          description: formData.description.trim() || undefined,
          icon: formData.icon.trim() || undefined,
          isActive: formData.isActive,
        });
        setSuccess(result.message);
      }

      handleCloseDialog();
      await fetchServiceTypes();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (serviceType: ServiceType) => {
    setServiceTypeToDelete(serviceType);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setServiceTypeToDelete(null);
  };

  // Confirm delete (deactivate)
  const handleConfirmDelete = async () => {
    if (!serviceTypeToDelete) return;

    try {
      setActionLoading(true);
      setError(null);
      const result = await adminServiceTypesApi.delete(serviceTypeToDelete._id);
      setSuccess(result.message);
      setDeleteDialogOpen(false);
      setServiceTypeToDelete(null);
      await fetchServiceTypes();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Seed initial service types
  const handleSeedServiceTypes = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const result = await adminServiceTypesApi.seed();
      setSuccess(result.message);
      await fetchServiceTypes();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Filter service types by search query
  const filteredServiceTypes = serviceTypes.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count active service types
  const activeCount = serviceTypes.filter((s) => s.isActive).length;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Category color="primary" />
            <Typography variant="h6">Service Types</Typography>
            <Chip label={`${activeCount} active`} size="small" color="primary" />
            {serviceTypes.length > activeCount && (
              <Chip
                label={`${serviceTypes.length - activeCount} inactive`}
                size="small"
                color="default"
              />
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Import default service types">
              <Button
                size="small"
                startIcon={<CloudDownload />}
                onClick={handleSeedServiceTypes}
                disabled={actionLoading}
              >
                Seed Defaults
              </Button>
            </Tooltip>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchServiceTypes}
              disabled={actionLoading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
              disabled={actionLoading}
            >
              Add Service Type
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search service types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Table */}
        {filteredServiceTypes.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              {searchQuery
                ? 'No service types match your search'
                : 'No service types yet. Click "Seed Defaults" to import initial service types.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServiceTypes.map((serviceType) => (
                  <TableRow
                    key={serviceType._id}
                    hover
                    sx={{ opacity: serviceType.isActive ? 1 : 0.6 }}
                  >
                    <TableCell>
                      <Typography variant="body2">{serviceType.order}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {serviceType.icon && (
                          <Typography>{serviceType.icon}</Typography>
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {serviceType.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                        {serviceType.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {serviceType.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={serviceType.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={serviceType.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(serviceType)}
                          disabled={actionLoading}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {serviceType.isActive && (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(serviceType)}
                            disabled={actionLoading}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingServiceType ? 'Edit Service Type' : 'Add Service Type'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Service types are shown in the registration dropdown for new organizations.
              </Typography>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                fullWidth
                placeholder="e.g., Life Coach"
                helperText="The display name for this service type"
                autoFocus
                required
              />
              <TextField
                label="Slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  }))
                }
                fullWidth
                placeholder="e.g., life-coach"
                helperText="URL-friendly identifier (auto-generated from name)"
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., Life, business, or career coaching services"
                helperText="Optional description for this service type"
              />
              <TextField
                label="Icon (Emoji)"
                value={formData.icon}
                onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
                fullWidth
                placeholder="e.g., (optional emoji)"
                helperText="Optional emoji icon to display"
              />
              {editingServiceType && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                      }
                    />
                  }
                  label="Active"
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={actionLoading || !formData.name.trim()}
              startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
            >
              {editingServiceType ? 'Save Changes' : 'Add Service Type'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Deactivate Service Type"
          message={`Are you sure you want to deactivate "${serviceTypeToDelete?.name}"? It will no longer appear in the registration dropdown.`}
          confirmText="Deactivate"
          cancelText="Cancel"
          variant="warning"
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteDialog}
          isLoading={actionLoading}
        />
      </CardContent>
    </Card>
  );
}
